import { configStore, uiStore } from "@/store/store";
import * as Twig from "twig";
import { Template } from "twig";
import { getHash } from "@/util/hash";
import { Tag } from "@/types/tags";
import defensiveCopy from "@/util/defensive-copy";
import {
  BuilderElement,
  BuilderElementAdditionalClass,
  BuilderElementAttributeTemplated,
  MacrosData
} from "@/types/elements";
import { isDefined } from "ts-is-present";
import { readFromLocalStorage, writeToLocalStorage } from "@/util/localstorage";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";
import { jsonParseOrUndefined } from "@/util/json-parse-or-undefined";

/**
 * A hash map containing the twig template functions
 *
 * Twig contains a cache of its own if we use the `id` param
 * The problem is that we need to know if the template was already ran or not
 * and we want to avoid the overhead of creating a new template when it already exists
 */
const twigTemplatesCache: { [key: string]: Template } = {};

export const TWIG_MACROS_CACHE_KEY = "breakdance-twig-macros";
export const TWIG_MACROS_HASH_CACHE_KEY = "breakdance-twig-macros-hash";

export function getBuilderElementTemplateCacheKey(el: BuilderElement) {
  return `element-${el.slug}-template`;
}

export function getBuilderElementCssTemplateCacheKey(el: BuilderElement) {
  return `element-${el.slug}-css-template`;
}

export function getBuilderElementAttributeTemplateCacheKey(
  el: BuilderElement,
  attr: BuilderElementAttributeTemplated
) {
  return `element-${el.slug}-attr-${attr.name}-template`;
}

export function getBuilderElementAdditionalClassTemplateCacheKey(
  el: BuilderElement,
  classDef: BuilderElementAdditionalClass
) {
  return `element-${el.slug}-add-class-${classDef.name}-template`;
}

export function precompileTemplatesOfSingleElement(el: BuilderElement) {
  const templateId = getBuilderElementTemplateCacheKey(el);
  const templateCache = compileTwigTemplate(
    prependMacrosToTemplate(el.template),
    templateId
  );

  if (!templateCache) return;

  const cssTemplateId = getBuilderElementCssTemplateCacheKey(el);
  const compiledTemplate = compileTwigTemplate(
    prependMacrosToTemplate(el.cssTemplate),
    cssTemplateId
  );

  if (!compiledTemplate) return;

  [...(el.additionalClasses || [])].forEach(addClass => {
    const addClassTemplateId = getBuilderElementAdditionalClassTemplateCacheKey(
      el,
      addClass
    );

    compileTwigTemplate(
      prependMacrosToTemplate(addClass.template),
      addClassTemplateId
    );
  });

  [
    ...(el.attributes || []),
    ...(configStore.elementsExternalAttrsDepsAndActions.attributes || [])
  ]
    .filter(
      (attr): attr is BuilderElementAttributeTemplated => "template" in attr
    )
    .forEach(templatedAttr => {
      const attrTemplateId = getBuilderElementAttributeTemplateCacheKey(
        el,
        templatedAttr
      );

      compileTwigTemplate(
        prependMacrosToTemplate(templatedAttr.template),
        attrTemplateId
      );
    });
}

export function loadCachedCompiledTwigMacrosTemplate(): Template | null {
  const localStorageCachedMacrosHash = readFromLocalStorage(
    TWIG_MACROS_HASH_CACHE_KEY
  );

  const cacheIsOutdated =
    !localStorageCachedMacrosHash ||
    localStorageCachedMacrosHash !== configStore.twigMacrosHash;

  if (cacheIsOutdated) {
    return null;
  }

  const allTwigMacrosPrecompiledCache = readFromLocalStorage(
    TWIG_MACROS_CACHE_KEY
  );

  if (
    allTwigMacrosPrecompiledCache === null ||
    allTwigMacrosPrecompiledCache === false
  ) {
    return null;
  }

  const parsedCache = jsonParseOrUndefined<Template>(
    allTwigMacrosPrecompiledCache
  );

  if (parsedCache === undefined) {
    return null;
  }

  return parsedCache;
}

export function precompileTwigMacrosTemplate() {
  const cachedCompiledTwigMacrosTemplate =
    loadCachedCompiledTwigMacrosTemplate();

  if (cachedCompiledTwigMacrosTemplate === null) {
    const allMacrosString = getAllTwigMacrosString(configStore.twigMacros);

    const compiledMacrosTemplate = compileTwigTemplate(
      allMacrosString,
      TWIG_MACROS_CACHE_KEY
    );

    if (compiledMacrosTemplate === null) {
      uiStore.logError("Unable to compile macros template");
      return;
    }

    const compiledMacrosTemplateAsJson = jsonStringifyOrUndefined({
      // @ts-ignore
      id: compiledMacrosTemplate.id,
      // @ts-ignore
      tokens: compiledMacrosTemplate.tokens
    });

    if (compiledMacrosTemplateAsJson && configStore.twigMacrosHash) {
      writeToLocalStorage(TWIG_MACROS_CACHE_KEY, compiledMacrosTemplateAsJson);
      writeToLocalStorage(
        TWIG_MACROS_HASH_CACHE_KEY,
        configStore.twigMacrosHash
      );
    }
  } else {
    twigTemplatesCache[TWIG_MACROS_CACHE_KEY] = Twig.twig({
      // @ts-ignore Because @types/twig doesn't reflect Template type has "id"
      id: cachedCompiledTwigMacrosTemplate.id,
      // @ts-ignore Because @types/twig doesn't reflect Template type has "tokens"
      data: cachedCompiledTwigMacrosTemplate.tokens,
      // @ts-ignore
      precompiled: true
    });
  }
}

export function precompileTemplatesOfElements(elements?: BuilderElement[]) {
  if (!(TWIG_MACROS_CACHE_KEY in twigTemplatesCache)) {
    console.warn(
      "Unable to precompile templates of elements – macros template is not compiled yet."
    );
    return;
  }

  elements = elements || Object.values(configStore.elements).filter(isDefined);

  elements.forEach(element =>
    // @ts-ignore
    requestIdleCallback(() => precompileTemplatesOfSingleElement(element))
  );
}

export function getAllTwigMacrosString(macros: MacrosData) {
  return macros.reduce((acc, macroData) => acc + macroData.macro, "");
}

export function prependMacrosToTemplate(twigTemplate: string): string {
  const twigTemplateWithMacrosPrepended = `
    {% import "${TWIG_MACROS_CACHE_KEY}" as macros %}
    ${twigTemplate}
    `;

  return twigTemplateWithMacrosPrepended;
}

export function compileTwigTemplate(
  twigTemplate: string,
  templateId: string
): Template | null {
  return tryCatchTwig(twigTemplate, () => {
    /**
     * This caching only applies to loading remote templates, which is not our case.
     * When this caching is enabled, Twig doesn't allow overwriting templates with the same ID,
     * which is required for template changes made at Element Studio to be reflected at rendered elements
     */
    Twig.cache(false);

    const templateFunction = Twig.twig({
      id: templateId,
      data: twigTemplate,
      // @ts-ignore It's not listed at the type declaration file that we use
      allowInlineIncludes: true,
      /* force Twig to throw exceptions if something goes wrong, rather than catch them and log the errors.
               We'll catch the exceptions during execution. */
      // @ts-ignore
      rethrow: true
    });

    twigTemplatesCache[templateId] = templateFunction;

    return templateFunction;
  });
}

function getTwigTemplateFromCacheOrCreateIt(
  twigTemplate: string,
  templateCacheKey?: string
) {
  const twigTemplateWithMacrosPrepended = prependMacrosToTemplate(twigTemplate);

  const twigTemplateHash =
    templateCacheKey ?? getHash(twigTemplateWithMacrosPrepended);

  if (twigTemplatesCache[twigTemplateHash]) {
    return twigTemplatesCache[twigTemplateHash];
  }

  const templateFunction = compileTwigTemplate(
    twigTemplateWithMacrosPrepended,
    twigTemplateHash
  );

  if (!templateFunction) return;

  return templateFunction;
}

export function renderTwig(
  twigTemplate: string,
  dataProps: object,
  templateCacheKey?: string
) {
  if (
    !twigTemplate.includes("{%") &&
    !twigTemplate.includes("{{") &&
    !twigTemplate.includes("{#")
  ) {
    return twigTemplate.trim();
  }

  return tryCatchTwig(twigTemplate, () => {
    const template = getTwigTemplateFromCacheOrCreateIt(
      twigTemplate,
      templateCacheKey
    );

    if (!template) return "";

    return (
      template
        // Twig can mutate the data object https://github.com/soflyy/breakdance/pull/1639
        .render({ ...defensiveCopy(dataProps), isBuilder: true })
        .trim()
    );
  });
}

function tryCatchTwig<T>(template: string, callback: () => T) {
  try {
    return callback();
  } catch (error) {
    uiStore.logConsoleError({
      error: error,
      template: template
    });

    return null;
  }
}

export function renderTags(
  template: string,
  tags: Partial<Record<Tag, string | number | null>>
): string {
  const tagsList = Object.keys(tags) as Tag[];

  return tagsList.reduce((acc, tag) => {
    const search = new RegExp(`%%${tag.toUpperCase()}%%`, "g");
    const value = tags[tag];
    return value !== null && value !== undefined
      ? acc.replace(search, String(value))
      : acc;
  }, template);
}
